import { applyStoreToMeta, isPersistentImageUrl, persistImageUrl } from './itemMeta.js'

export function formatEuroFr(value) {
  const numeric = Number(value)
  const safe = Number.isFinite(numeric) ? numeric : 0
  const [intPart, frac = '00'] = Math.abs(safe).toFixed(2).split('.')
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f')
  const sign = safe < 0 ? '-' : ''
  return `${sign}${grouped},${frac}\u00a0€`
}

export function pricesEqual(left, right) {
  const toCents = (value) => Math.round((Number(value) || 0) * 100)
  return toCents(left) === toCents(right)
}

export function padCurrencyChars(fromValue, toValue) {
  const fromText = formatEuroFr(fromValue)
  const toText = formatEuroFr(toValue)
  const width = Math.max(fromText.length, toText.length)
  return {
    fromChars: [...fromText.padStart(width, '\u2007')],
    toChars: [...toText.padStart(width, '\u2007')],
  }
}

export function isDigitChar(char) {
  return char >= '0' && char <= '9'
}

export function rollingColumns(fromValue, toValue) {
  const { fromChars, toChars } = padCurrencyChars(fromValue, toValue)
  return fromChars.map((fromChar, index) => {
    const toChar = toChars[index]
    const fromDigit = isDigitChar(fromChar)
    const toDigit = isDigitChar(toChar)
    if (fromDigit || toDigit) {
      return {
        type: 'digit',
        from: fromDigit ? Number(fromChar) : 0,
        to: toDigit ? Number(toChar) : 0,
      }
    }
    return {
      type: 'static',
      text: toChar === '\u2007' ? '' : toChar,
    }
  })
}

function convertObservedJpy(observed, rate) {
  const amount = Number(observed)
  const value = Number(rate)
  if (!(amount > 0) || !(value > 0) || value === 1) return null
  return Math.round(amount * value * 100) / 100
}

export function buildEditItemPayload(draft, extras = {}) {
  const payload = {
    name: String(draft?.name || '').trim(),
    link: String(draft?.link || '').trim(),
    price: Number(draft?.price) || 0,
    quantity: Number.parseInt(draft?.quantity, 10) || 1,
    promo: draft?.promo || '',
  }
  const current = extras.item || {}
  const nextImage = persistImageUrl(draft?.image_url)
  const imageChanged = Boolean(
    nextImage
    && nextImage !== persistImageUrl(current.image_url)
    && isPersistentImageUrl(nextImage),
  )
  if (imageChanged) {
    payload.image_url = nextImage
    if (draft?.image_key) payload.image_key = draft.image_key
  }
  const item = extras.item
  const conversion = extras.conversion
  const explicit = draft?.currency === 'JPY' || draft?.currency === 'EUR'
  if (!explicit && !item?.item_meta) return payload

  const currency = draft?.currency === 'JPY' ? 'JPY' : 'EUR'
  const observed = Number(draft?.price)
  let meta = item?.item_meta && typeof item.item_meta === 'object'
    ? { ...item.item_meta }
    : {}
  meta.observed_currency = currency
  meta.price = {
    ...(meta.price && typeof meta.price === 'object' ? meta.price : {}),
    observed: Number.isFinite(observed) ? observed : null,
    currency,
    converted_currency: 'EUR',
  }

  if (currency === 'EUR') {
    const amount = Number.isFinite(observed) && observed > 0 ? observed : 0
    payload.price = amount
    meta.observed_price_eur = amount
    meta.converted_price_eur = amount
    meta.conversion_status = 'direct'
    meta.conversion_stale = false
    meta.exchange_rate = null
    meta.exchange_rate_date = ''
    meta.price.converted = amount
    meta.price.exchange_rate = null
    if (draft?.storeDirty) {
      meta = applyStoreToMeta(meta, draft.store)
    }
    payload.item_meta = meta
    return payload
  }

  const rate = Number(conversion?.rate)
  const usableRate = rate > 0 && rate !== 1 ? rate : null
  const converted = convertObservedJpy(observed, usableRate)
  payload.price = converted || 0
  meta.observed_price_jpy = Number.isFinite(observed) && observed > 0 ? observed : null
  meta.converted_price_eur = converted
  meta.exchange_rate = converted ? usableRate : (Number(meta.exchange_rate) > 0 && Number(meta.exchange_rate) !== 1 ? Number(meta.exchange_rate) : null)
  meta.exchange_rate_date = converted
    ? String(conversion?.date || conversion?.retrieved_at || meta.exchange_rate_date || '')
    : String(meta.exchange_rate_date || '')
  meta.conversion_status = converted
    ? (conversion?.stale ? 'cached' : 'live')
    : 'pending'
  meta.conversion_stale = Boolean(conversion?.stale) && Boolean(converted)
  meta.price.converted = converted
  meta.price.exchange_rate = meta.exchange_rate
  meta.price.exchange_rate_date = meta.exchange_rate_date
  if (draft?.storeDirty) {
    meta = applyStoreToMeta(meta, draft.store)
  }
  payload.item_meta = meta
  return payload
}

export function mergeUpdatedItem(list, itemId, data) {
  const items = Array.isArray(list) ? list : []
  if (!itemId || !data || typeof data !== 'object') return items
  return items.map((item) => (
    item.id === itemId ? { ...item, ...data } : item
  ))
}

export function nextEditProductUiState({ error, fromPrice, toPrice } = {}) {
  if (error) {
    return {
      keepForm: true,
      error: error.message || error.detail || 'Impossible d’enregistrer le produit.',
      priceRoll: null,
    }
  }
  const from = Number(fromPrice) || 0
  const to = Number(toPrice) || 0
  return {
    keepForm: false,
    error: '',
    priceRoll: pricesEqual(from, to) ? null : { from, to },
  }
}
